import { Component } from "@theme/component";
import { sectionRenderer } from "@theme/section-renderer";
import { requestIdleCallback, viewTransition } from "@theme/utilities";
import { ThemeEvents } from "@theme/events";
import { PaginatedListAspectRatioHelper } from "@theme/paginated-list-aspect-ratio";

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {number | null} */
  #lastSyncedViewportPage = null;

  /** @type {number | null} */
  #syncURLAnimationFrame = null;

  /** @type {Set<number>} */
  #renderedPages = new Set();

  /** @type {Set<number>} */
  #pendingRenderPages = new Set();

  /** @type {Map<number, number>} */
  #missingGridPageAttempts = new Map();

  connectedCallback() {
    super.connectedCallback();

    // Normalize initial card page markers so next/previous calculations stay correct.
    const firstCard = Array.isArray(this.refs.cards)
      ? this.refs.cards[0]
      : undefined;
    const currentURL = new URL(window.location.href);
    const explicitPageFromURL = Number(
      currentURL.searchParams.get(this.pageParam) || "",
    );
    const explicitPageFromServer = Number(firstCard?.dataset.page || "");
    const initialPage = Number(
      Number.isFinite(explicitPageFromServer) && explicitPageFromServer > 0
        ? explicitPageFromServer
        : Number.isFinite(explicitPageFromURL) && explicitPageFromURL > 0
          ? explicitPageFromURL
          : "1",
    );
    if (Array.isArray(this.refs.cards)) {
      this.refs.cards.forEach((card) => {
        card.dataset.page = String(initialPage);
      });
    }

    this.#refreshRenderedPages();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage("next");
    this.#fetchPage("previous");
    this.#observeViewMore();

    window.addEventListener("scroll", this.#queueURLSync, { passive: true });
    window.addEventListener("resize", this.#queueURLSync, { passive: true });

    // Listen for filter updates to clear cached pages
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    window.removeEventListener("scroll", this.#queueURLSync);
    window.removeEventListener("resize", this.#queueURLSync);
    if (this.#syncURLAnimationFrame !== null) {
      cancelAnimationFrame(this.#syncURLAnimationFrame);
      this.#syncURLAnimationFrame = null;
    }

    // Remove the filter update listener
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  #queueURLSync = () => {
    if (this.#syncURLAnimationFrame !== null) return;

    this.#syncURLAnimationFrame = requestAnimationFrame(() => {
      this.#syncURLAnimationFrame = null;
      this.#syncURLToViewportPage();
      this.#renderNextPageFromScrollPosition();
    });
  };

  #renderNextPageFromScrollPosition() {
    if (!this.#isVisibleForInfiniteScroll()) return;

    const { viewMoreNext } = this.refs;

    if (!(viewMoreNext instanceof HTMLElement)) return;

    const distanceFromBottom =
      document.documentElement.scrollHeight -
      (window.scrollY + window.innerHeight);

    // Fallback for cases where IntersectionObserver misses the sentinel.
    if (distanceFromBottom > 200) return;

    this.#renderNextPage();
  }

  #isVisibleForInfiniteScroll() {
    return this.getClientRects().length > 0;
  }

  #syncURLToViewportPage = () => {
    if (!this.shouldSyncURL) return;

    const { cards } = this.refs;

    if (!Array.isArray(cards) || cards.length === 0) return;

    const viewportHeight = window.innerHeight;
    /** @type {Map<number, number>} */
    const visibleAreaByPage = new Map();

    cards.forEach((card) => {
      const rect = card.getBoundingClientRect();
      const visibleArea = Math.max(
        0,
        Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0),
      );

      if (visibleArea <= 0) return;

      const page = Number(card.dataset.page);
      if (!Number.isFinite(page) || page < 1) return;

      const currentArea = visibleAreaByPage.get(page) || 0;
      visibleAreaByPage.set(page, currentArea + visibleArea);
    });

    if (visibleAreaByPage.size === 0) return;

    let visiblePage = null;
    let largestVisibleArea = -1;

    visibleAreaByPage.forEach((area, page) => {
      if (area <= largestVisibleArea) return;

      largestVisibleArea = area;
      visiblePage = page;
    });

    if (visiblePage === null) return;

    if (!Number.isFinite(visiblePage) || visiblePage < 1) return;

    if (this.#lastSyncedViewportPage === visiblePage) return;

    this.#lastSyncedViewportPage = visiblePage;

    const nextURL = this.#buildPageURL(visiblePage);
    history.replaceState("", "", nextURL.toString());
  };

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: "100px",
        },
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  #refreshRenderedPages() {
    this.#renderedPages.clear();

    const { cards } = this.refs;
    if (!Array.isArray(cards)) return;

    cards.forEach((card) => {
      const page = Number(card.dataset.page);
      if (!Number.isFinite(page) || page < 1) return;

      this.#renderedPages.add(page);
    });
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage))
      return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === "next") {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    try {
      if (!page || !this.#shouldUsePage(page)) {
        return;
      }

      await this.#fetchSpecificPage(page.page, page.url);
    } catch {
      // Ignore transient fetch failures; scrolling/intersection can retry.
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageURL = url || this.#buildPageURL(pageNumber);
    const pageInfo = { page: pageNumber, url: pageURL };

    if (!this.#shouldUsePage(pageInfo)) return;
    const sectionRequestURL = this.#buildSectionRequestURL(pageURL, pageNumber);
    const pageContent = await sectionRenderer.getSectionHTML(
      this.sectionId,
      true,
      sectionRequestURL,
    );
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage("next");

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    if (
      this.#renderedPages.has(nextPage.page) ||
      this.#pendingRenderPages.has(nextPage.page)
    ) {
      return;
    }

    this.#pendingRenderPages.add(nextPage.page);

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    try {
      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage("next");

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) {
          this.#handleMissingGridPage("next", nextPage.page);
          return;
        }
      }

      this.#missingGridPageAttempts.delete(nextPage.page);

      nextPageItemElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.dataset.page = String(nextPage.page);
        }
      });

      grid.append(...nextPageItemElements);
      this.#renderedPages.add(nextPage.page);
      this.#pairNaturalItems();

      this.#aspectRatioHelper?.processNewElements();
      this.#queueURLSync();

      requestIdleCallback(() => {
        this.#fetchPage("next");
      });
    } finally {
      this.#pendingRenderPages.delete(nextPage.page);
    }
  }

  /**
   * @param {"previous" | "next"} direction
   * @param {number} page
   */
  #handleMissingGridPage(direction, page) {
    const attempts = (this.#missingGridPageAttempts.get(page) || 0) + 1;
    this.#missingGridPageAttempts.set(page, attempts);

    if (attempts < 3) {
      requestIdleCallback(() => {
        if (direction === "next") {
          this.#renderNextPage();
        } else {
          this.#renderPreviousPage();
        }
      });
      return;
    }

    // Skip persistently invalid pages so infinite scrolling can continue.
    this.#renderedPages.add(page);
    this.#missingGridPageAttempts.delete(page);

    requestIdleCallback(() => {
      if (direction === "next") {
        this.#fetchPage("next");
        this.#renderNextPage();
      } else {
        this.#fetchPage("previous");
        this.#renderPreviousPage();
      }
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage("previous");
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    if (
      this.#renderedPages.has(previousPage.page) ||
      this.#pendingRenderPages.has(previousPage.page)
    ) {
      return;
    }

    this.#pendingRenderPages.add(previousPage.page);

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    try {
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage("previous");

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      previousPageItemElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.dataset.page = String(previousPage.page);
        }
      });

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement
        ? firstElement.getBoundingClientRect().top + window.scrollY
        : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);
      this.#renderedPages.add(previousPage.page);
      this.#pairNaturalItems();

      this.#aspectRatioHelper?.processNewElements();
      this.#queueURLSync();

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight =
          firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: "instant",
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage("previous");
      });
    } finally {
      this.#pendingRenderPages.delete(previousPage.page);
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === "previous";

    const renderedPages = Array.from(this.#renderedPages).filter(
      (page) => Number.isFinite(page) && page > 0,
    );

    let currentPage = null;

    if (renderedPages.length > 0) {
      currentPage = isPrevious
        ? Math.min(...renderedPages)
        : Math.max(...renderedPages);
    } else {
      const { cards } = this.refs;
      if (!Array.isArray(cards) || cards.length === 0) return;

      const targetCard = cards[isPrevious ? 0 : cards.length - 1];
      const currentCardPage = Number(targetCard?.dataset.page);
      if (!Number.isFinite(currentCardPage) || currentCardPage < 1) return;

      currentPage = currentCardPage;
    }

    const page = isPrevious ? currentPage - 1 : currentPage + 1;

    return {
      page,
      url: this.#buildPageURL(page),
    };
  }

  /**
   * @param {number} page
   * @returns {URL}
   */
  #buildPageURL(page) {
    const url = new URL(window.location.href);
    const pageValue = String(page);

    url.searchParams.set(this.pageParam, pageValue);

    if (this.serverPageParam && this.serverPageParam !== this.pageParam) {
      url.searchParams.delete(this.serverPageParam);
    } else if (this.pageParam !== "page") {
      url.searchParams.delete("page");
    }

    if (this.viewParam && this.viewValue) {
      url.searchParams.set(this.viewParam, this.viewValue);
    }

    url.hash = "";
    return url;
  }

  /**
   * @param {URL} historyURL
   * @param {number} page
   * @returns {URL}
   */
  #buildSectionRequestURL(historyURL, page) {
    const requestURL = new URL(historyURL.toString());

    if (this.serverPageParam && this.serverPageParam !== this.pageParam) {
      requestURL.searchParams.set(this.serverPageParam, String(page));
    }

    return requestURL;
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, "text/html");
    // A section can hold more than one list (Tabbed collections has one per tab). A list with an
    // id reads only its own grid from the fetched section, not the first one in it.
    const scope = this.id ? parsedPage.getElementById(this.id) : parsedPage;
    const gridElement = scope?.querySelector('[ref="grid"]');
    if (!gridElement) return;
    // Collection ads sit among the cards in page order. They aren't cards: nothing else here
    // pages, restores or sizes by them, so they come in by their own marker.
    return gridElement.querySelectorAll(
      ':scope > :is([ref="cards[]"], [data-collection-ad])',
    );
  }

  /**
   * Pairs a natural list's cards and ads up again across its pages. resource-list pairs them
   * into rows of two on mobile, each by its share of the pair's combined media ratio, but it only
   * sees one page: the last item of a page with an odd count is left unpaired, at half a row, and
   * the page after it pairs from its own first item. Once pages are joined here, the pairs run on
   * through the whole list, so no row is left half empty where two pages meet.
   */
  #pairNaturalItems() {
    const { grid } = this.refs;
    if (!grid?.classList.contains("resource-list--natural")) return;

    const items = /** @type {HTMLElement[]} */ ([
      ...grid.querySelectorAll(
        ':scope > :is([ref="cards[]"], [data-collection-ad])',
      ),
    ]);
    const ratios = items.map((item) => {
      const property = item.hasAttribute("data-collection-ad")
        ? "--collection-ad-ratio"
        : "--natural-card-ratio";
      return parseFloat(item.style.getPropertyValue(property)) || 1;
    });

    for (let index = 0; index < items.length; index += 2) {
      const item = items[index];
      const partner = items[index + 1];
      const ratio = ratios[index] ?? 1;
      if (!item) continue;

      const pairRatio = partner ? ratio + (ratios[index + 1] ?? 1) : ratio * 2;
      for (const pairItem of [item, partner]) {
        pairItem?.style.setProperty("--natural-pair-ratio", String(pairRatio));
        pairItem?.style.removeProperty("--natural-card-grow");
      }
      if (!partner) item.style.setProperty("--natural-card-grow", "0");
    }
  }

  /**
   * Appends the next page, for lists that load more on a click rather than on scroll.
   * @returns {Promise<void>}
   */
  renderNextPage() {
    return this.#renderNextPage();
  }

  /** Whether a page follows the last one rendered. */
  get hasNextPage() {
    return this.#shouldUsePage(this.#getPage("next"));
  }

  get sectionId() {
    const id = this.getAttribute("section-id");

    if (!id) throw new Error("The section-id attribute is required");

    return id;
  }

  get pageParam() {
    return this.getAttribute("page-param") || "page";
  }

  get serverPageParam() {
    return this.getAttribute("server-page-param") || "";
  }

  get viewParam() {
    return this.getAttribute("view-param") || "";
  }

  get viewValue() {
    return this.getAttribute("view-value") || "";
  }

  get shouldSyncURL() {
    return this.getAttribute("sync-url") !== "false";
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#pendingRenderPages.clear();
    this.#renderedPages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#refreshRenderedPages();
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage("next");
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ["data-last-page"],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
